//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  BulkUploadErrorSource,
  BulkUploadProductError,
} from '@campaigns/types/sampling/samplingCampaignBulkUploadTypes';
import { downloadErrorReport } from '@campaigns/utils/sampling/samplingCampaignBulkUploadUtils';
import { Alert } from '@components/atoms/alert/Alert';
import {
  PrimaryLinkButton,
  TertiaryButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { BodyText } from '@components/atoms/text/Text';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { isDefined } from '@utils/filterPredicates';

interface ErrorViewProps {
  errors: BulkUploadProductError[];
  onReplace: () => void;
  fileName?: string;
}

export const ErrorView = ({ errors, fileName, onReplace }: ErrorViewProps) => {
  const timeZone = useSelector(getUserTimeZone);

  const firstError = errors[0];
  const shouldShowInlineErrors =
    isDefined(firstError) &&
    (firstError.source === BulkUploadErrorSource.FILE ||
      firstError.source === BulkUploadErrorSource.BACKEND);

  return (
    <StyledStateContainer>
      <Alert type='error'>Something wasn't quite right with your file</Alert>
      {shouldShowInlineErrors ? (
        <ErrorList>
          {firstError.errors.map((error, index) => (
            <li key={`${error}-${index}`}>
              <BodyText>{error}</BodyText>
            </li>
          ))}
        </ErrorList>
      ) : (
        <StyledButtonContainer>
          <StyledIcon name='file' />
          <PrimaryLinkButton
            onClick={() => downloadErrorReport(errors, timeZone, fileName)}
          >
            Download Error Report
          </PrimaryLinkButton>
        </StyledButtonContainer>
      )}
      <StyledTertiaryButton onClick={onReplace}>
        Replace file
      </StyledTertiaryButton>
    </StyledStateContainer>
  );
};

const StyledStateContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[4]};
  `,
);

const StyledTertiaryButton = styled(TertiaryButton)`
  align-self: center;
`;

const StyledButtonContainer = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: center;
  `,
);

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    width: ${theme.spacings[4]};
    height: ${theme.spacings[4]};
  `,
);

const ErrorList = styled.ul(
  ({ theme }) => `
    padding: 0 0 0 ${theme.spacings[4]};
    margin: 0;
    list-style-type: disc;
  `,
);
